<script setup>
import {computed, ref, useSlots} from "vue";
import generateId from "@/composables/generateId";
import IconSearch from "@/assets/icons/IconSearch.vue";
import IconCross from "@/assets/icons/IconCross.vue";


const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },
  label: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: ""
  },
  alert: {
    type: String,
    default: ""
  },
  error: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: "text",
    validator: (value) => ["text", "email", "password", "number", "tel", "url"].includes(value)
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["small", "medium", "large"].includes(value)
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  useSearchIcon: {
    type: Boolean,
    default: true
  },
  useCrossIcon: {
    type: Boolean,
    default: true
  }
});

const inputId = generateId();

const emit = defineEmits(["update:modelValue", "focus", "blur", "input", "change", "suffix-click", "clear-input"]);

const isFocused = ref(false);
const inputRef = ref(null);

const inputWrapperClasses = computed(() => [
  `input-wrapper--${props.size}`,
  {
    "input-wrapper--focused": isFocused.value,
    "input-wrapper--disabled": props.disabled,
    "input-wrapper--error": props.error,
    "input-wrapper--suffix": slots["suffix"]
  }
]);

const handleFocus = (event) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleBlur = (event) => {
  isFocused.value = false;
  emit("blur", event);
};

const clearInput = () => {
  emit("update:modelValue", "");
  emit("input", { target: { value: "" } });
  emit("change", { target: { value: "" } });
  emit("clear-input");
  emit("suffix-click");
};

const localModelValue = computed(({
  set(value) {
    emit("update:modelValue", value);
  },
  get() {
    return props.modelValue;
  }
}));

const focus = () => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({
  focus
});

const slots = useSlots();
</script>

<template>
  <div class="input-container">
    <label
      v-if="props.label"
      :for="inputId"
      class="input-container__label"
    >
      {{ props.label }}
      <span
        v-if="required"
        class="input-container__label--required"
      >*</span>
    </label>
    <div
      :class="inputWrapperClasses"
      class="input-wrapper"
    >
      <input
        ref="inputRef"
        v-model="localModelValue"
        class="input-wrapper__input"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :type="props.type"
        :autofocus="autofocus"
        @focus="handleFocus"
        @blur="handleBlur"
      >
      <div
        class="input-wrapper__icon"
        :class="{ 'input-wrapper__icon--clickable': modelValue.length > 0,
                  'input-wrapper__icon--focused': modelValue.length === 0 && isFocused }"
        @click="modelValue.length > 0 ? clearInput() : null"
      >
        <IconSearch v-if="modelValue.length === 0 && useSearchIcon" />
        <IconCross v-else-if="modelValue.length > 0 && useCrossIcon" />
      </div>
      <div
        v-if="slots['suffix']"
        class="input-wrapper__suffix"
      >
        <slot name="suffix" />
      </div>
    </div>
    <div
      v-if="props.alert"
      class="input-container__alert"
    >
      {{ props.alert }}
    </div>
  </div>
</template>

<style scoped lang="scss">

.input-container {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__label {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
    color: var(--form-primary-title-text);

    &--required { color: var(--system-red); }
  }

  &__alert {
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    color: var(--form-primary-placeholder-text)
  }
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: var(--background-01);
  padding: 0 12px;
  border-radius: 8px;
  color: var(--text-text-main);

  &--suffix {
    padding: 0 6px 0 10px;
  }

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 14px;
    height: 14px;
    color: var(--icon-icon-secondary);

    &--clickable {
      cursor: pointer;
    }

    &--focused {
      color: var(--icon-icon-on);
    }
  }

  &:hover:not(.input-wrapper--focused):not(.input-wrapper--disabled) {
    background-color: var(--background-bg-hover2);
  }

  &--focused {
    background-color: var(--background-01);
  }

  &--error {
    border-color: var(--border-bd-error);
    background-color: var(--form-primary-default);
  }

  &--disabled {
    background-color: var(--form-primary-disabled);
    color: var(--text-text-disabled);
  }

  &__input {
    all: unset;
    width: 100%;
    height: 100%;
  }

  // Размеры
  &--small { height: 30px; }

  &--medium { height: 36px; }

  &--large { height: 42px; }

}

</style>